import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Quiz } from './Quiz';
import CorrectionEval from './CorrectionEval';
import IleEleve from './ile/IleEleve';
import ResultatViewEleve from './ResultatViewEleve';
import HomeEleve from './HomeEleve';
import MoiEleve from './MoiEleve';
import TabBarEleve from './TabBarEleve';
import BlasonsEleve from './BlasonsEleve';
import XpRecapOverlay from '../../components/Eleve/XpRecapOverlay';
import { useXp } from '../../context/XpContext';
import { progressionRatio, DEFIS_QUOTA_JOUR } from '../../utils/xpConfig';
import { marquerQuizDuJour } from '../../utils/rituel';
import HeaderEleve from './HeaderEleve';
import FooterEleve from './FooterEleve';
import { useEvalNat } from '../../hooks/fetch_api_hook/useEvalNat';
import { useSessionsQCM } from '../../hooks/fetch_api_hook/useSessionsQCM';
import { useClasseContext } from '../../context/ClasseContext';
import { useUser } from '../../context/UserContext';
import { resolveBonneReponse } from '../../utils/questionUtils';
import { matiereKey } from '../../utils/quizEleveStatus';
import { tirerDefi, niveauDepuisClasse, defisEpuises, defisCreditesAffiches } from '../../utils/defisQuotidiens';
import { peutRejouer, premierEssai, questionsDuRejeu } from '../../utils/rejeuQuiz';
import { generateExercice } from '../../generators';
import axios from 'axios';
import Cookies from 'js-cookie';
import { url } from '../../utils/confserveur';

// Labels des types de questions pour le groupement par catégorie
const TYPE_LABELS = { qcm: 'QCM', texte: 'Saisie', association: 'Association' };

// Onglet de la tabbar correspondant à chaque vue « maison ».
const TAB_DE_VUE = { dashboard: 'jouer', quizz: 'ile', blasons: 'blasons', moi: 'moi' };

/**
 * Espace élève — orchestrateur. Style « app » : vues maison (Jouer/Île/Blasons/
 * Moi/Résultats) avec une tabbar persistante ; les activités plein écran
 * (quiz, correction) s'affichent sans tabbar. La structure quiz/questions/
 * réponses/correction/scoring/soumission/auth-GAR n'est PAS modifiée.
 */
const EspaceEleve = ({ eleveBDD, isGarEleve = false }) => {
  // Fiche unique (spec GAR §3.3) : UNE fiche pour tout — isGarEleve vient de
  // l'API (get-sync-status) via RootEleve, plus de copie localStorage.

  // Pour un élève GAR, afficher le prénom GAR
  const displayPrenom = isGarEleve && eleveBDD.eleve_gar_prenom ? eleveBDD.eleve_gar_prenom : eleveBDD.prenom;
  const { user, matiereFilter, init } = useUser();
  const { classeNom, logout: contextLogout } = useClasseContext();
  const { profil: xpProfil, recap, clearRecap } = useXp();
  const navigate = useNavigate();

  // Fonction de déconnexion (réservée aux élèves non-GAR — cf. HomeEleve)
  const handleLogout = async () => {
    try {
      const csrfToken = Cookies.get('csrftoken');
      await axios.post(
        `${url}logout/`,
        {},
        { withCredentials: true, headers: { 'X-CSRFToken': csrfToken } },
      );
      contextLogout?.();
      init?.();
      localStorage.clear();
      navigate('/');
      window.location.reload();
    } catch (error) {
      console.error('Erreur lors de la déconnexion :', error);
    }
  };

  // ── Navigation ──
  // 'dashboard'(=Jouer) | 'quizz'(=Île) | 'resultats' | 'blasons' | 'moi' | 'playing' | 'correction'
  const [activeView, setActiveView] = useState('dashboard');

  // ── Évaluations ──
  const [launch, setLaunch] = useState(false);
  const [launchCorrection, setLaunchCorrection] = useState(false);
  const [questionsQuiz, setQuestionsQuiz] = useState('');
  const [evalBilan, setEvalBilan] = useState();
  const [listEvalBilan, setListEvalBilan] = useState([]);

  // Fetch des évaluations (classe + groupes de l'élève)
  const { data: EvalsNat } = useEvalNat(`?user_id=${user.id_user}`, null, launch);
  // Fetch des sessions QCM en ligne accessibles à cet élève
  const { data: sessionsEnLigne } = useSessionsQCM(
    { eleve_id: user.id_user, mode_passation: 'en_ligne' },
    launch,
  );

  // Filter evaluations — le backend filtre déjà par user_id (classe + groupes).
  // Fiche unique : plus de gating par matière synchronisée (l'élève GAR a UNE
  // fiche qui couvre tout), fin du fallback croisé getEleveIdForEval.
  useEffect(() => {
    if (!launch) {
      const filteredEvals = EvalsNat?.filter((e) => {
        if (e.titre === 'Evaluation Nationale') return false;
        if (!isGarEleve && matiereFilter) {
          if (e.matiere !== matiereFilter.toLowerCase()) return false;
        }
        return true;
      }) || [];
      setListEvalBilan(filteredEvals);
    }
  }, [EvalsNat, launch, matiereFilter, isGarEleve]);

  // Vérifier si l'élève a répondu à une évaluation (par ID pour éviter les homonymes)
  const verifierSiEleveARepondu = useCallback((item) => {
    const eleve = item.eleves_reponses?.find((e) => String(e.id_eleve) === String(eleveBDD.id));
    return !!eleve?.reponses;
  }, [eleveBDD.id]);

  // Fusion bilans + sessions en ligne, triées par date (plus récent en premier)
  const allQuizz = useMemo(() => {
    const bilans = (listEvalBilan || []).map((e) => ({ ...e, source: 'bilan', sortDate: e.date || '' }));
    const sessions = (sessionsEnLigne || []).map((s) => ({ ...s, source: 'session', sortDate: s.date_passation || '' }));
    return [...bilans, ...sessions].sort((a, b) => b.sortDate.localeCompare(a.sortDate));
  }, [listEvalBilan, sessionsEnLigne]);

  // Stats globales (bilans + sessions) — utilisées par la vue Résultats
  const stats = useMemo(() => {
    const completed = allQuizz.filter((e) => verifierSiEleveARepondu(e)).length;
    const pending = allQuizz.length - completed;
    const successRate = allQuizz.length > 0 ? Math.round((completed / allQuizz.length) * 100) : 0;
    return { completed, pending, total: allQuizz.length, successRate };
  }, [allQuizz, verifierSiEleveARepondu]);

  // Compteurs de quiz EN ATTENTE par matière (pour la Home).
  const counts = useMemo(() => {
    let maths = 0;
    let francais = 0;
    allQuizz.forEach((q) => {
      if (verifierSiEleveARepondu(q)) return;
      const k = matiereKey(q.matiere);
      if (k === 'maths') maths += 1;
      else if (k === 'francais') francais += 1;
    });
    return { maths, francais };
  }, [allQuizz, verifierSiEleveARepondu]);

  // Prochain quiz non répondu (le plus récent) — pour la mission du jour.
  const nextQuiz = useMemo(
    () => allQuizz.find((q) => !verifierSiEleveARepondu(q)) || null,
    [allQuizz, verifierSiEleveARepondu],
  );

  // Marque locale « un quiz a été joué aujourd'hui » (repli de la sauvegarde
  // de flamme quand les transactions ne sont pas encore rafraîchies).
  useEffect(() => {
    if (recap) marquerQuizDuJour();
  }, [recap]);

  // Transformer les questions pour le quiz
  const getFilteredQuestions = (questionKeys) => {
    const filteredQuestions = {};
    questionKeys.forEach((question) => {
      if (!filteredQuestions[question.type_label]) filteredQuestions[question.type_label] = {};
      filteredQuestions[question.type_label][question.id] = question;
    });
    return filteredQuestions;
  };

  // ── Handlers (inchangés) ──
  const handleStartQuiz = (e) => {
    if (!e?.questions || e.questions.length === 0) return; // éval sans questions → injouable
    setQuestionsQuiz(getFilteredQuestions(e.questions));
    setEvalBilan(e);
    setLaunch(true);
    setActiveView('playing');
  };

  // Lancer une session QCM en ligne — groupement par descripteur_titre
  const handleStartSession = useCallback((session) => {
    if (!session.questions || session.questions.length === 0) return;
    if (session.statut === 'terminee') return;
    const formatted = {};
    (session.questions || []).forEach((sq) => {
      const q = sq.banque_question || sq;
      const label = q.descripteur_titre || q.attendu_titre || TYPE_LABELS[q.type] || 'Questions';
      if (!formatted[label]) formatted[label] = {};
      formatted[label][q.id] = {
        id: q.id,
        type_label: label,
        type_question: q.type,
        texte: q.texte,
        contenu: q.contenu,
        bonne_reponse: resolveBonneReponse(q.contenu),
        elements_additionnels: q.elements_additionnels,
        // Provenance exerciseur (le nested BanqueQuestionSerializer les envoie) :
        // alimente le filigrane de trace du Quiz + la soumission
        attendu: q.attendu ?? null,
        generator_key: q.generator_key ?? null,
      };
    });
    setQuestionsQuiz(formatted);
    setEvalBilan({ ...session, source: 'session' });
    setLaunch(true);
    setActiveView('playing');
  }, []);

  // Lancer un DÉFI QUOTIDIEN — questions générées LOCALEMENT par les
  // exerciseurs du thème (zéro appel réseau). Clés locales defi_i : le moteur
  // Quiz n'exige pas d'id DB, seule la soumission diffère (source 'defi').
  // Renvoie true si le défi est réellement lancé — l'appelant (VolcanRegion)
  // ne pose ses flags de retour-cratère QUE dans ce cas (sinon flags
  // orphelins → catapultage fantôme dans le cratère au prochain montage).
  const handleStartDefi = useCallback((theme) => {
    if (defisEpuises(xpProfil)) return false; // plafond atteint — l'Île affiche déjà le repos
    const payloads = tirerDefi(theme, 5);
    if (payloads.length === 0) return false; // thème sans générateur exploitable
    const label = `Défi — ${theme.nom}`;
    const formatted = { [label]: {} };
    payloads.forEach((p, i) => {
      formatted[label][`defi_${i}`] = {
        id: `defi_${i}`,
        type_label: label,
        type_question: p.type,
        texte: p.texte,
        contenu: p.contenu,
        bonne_reponse: resolveBonneReponse(p.contenu),
        elements_additionnels: p.elements_additionnels,
        question_num: i + 1,
        // Provenance pour le payload de soumission (contrat defis-quotidiens)
        attendu: p.attendu ?? null,
        generator_key: p._meta?.generatorKey || null,
      };
    });
    setQuestionsQuiz(formatted);
    setEvalBilan({
      source: 'defi',
      titre: label,
      correction_visible: true, // feedback Duolingo après chaque réponse
      theme: theme.nom,
      niveau: niveauDepuisClasse(classeNom),
      matiere: 'maths', // générateurs maths only aujourd'hui
      ambiance: 'volcan', // le quiz garde le fond du cratère (région maths)
    });
    setLaunch(true);
    setActiveView('playing');
    return true;
  }, [classeNom, xpProfil]);

  // REJOUER un quiz en ligne (décision du fondateur, 04/10) : un rejeu est un
  // défi (correction en direct, barème des défis, un des crédités du jour) qui
  // porte `session_id`, le quiz entier, ses questions de générateur retirées au
  // sort. L'Île l'offre dès la copie rendue ; tant que le prof masque la
  // correction, les questions reprises à l'identique ne disent que juste ou faux.
  const peutRejouerQuiz = useCallback((q) => peutRejouer(q, eleveBDD.id), [eleveBDD.id]);
  const handleStartRejeu = useCallback((session) => {
    if (defisCreditesAffiches(xpProfil) >= DEFIS_QUOTA_JOUR) return; // l'Île grise déjà « Rejouer »
    const formatted = questionsDuRejeu(session, generateExercice);
    const label = Object.keys(formatted)[0];
    if (!label) return;
    setQuestionsQuiz(formatted);
    setEvalBilan({
      source: 'defi',
      rejeuDe: session.id,
      titre: label,
      correction_visible: true,
      // Thème et matière : le back impose les siens (« Rejeu : titre du quiz », la matière du quiz).
      theme: label,
      niveau: niveauDepuisClasse(classeNom),
      matiere: session.matiere || 'maths',
      premierEssai: premierEssai(session, eleveBDD.id),
    });
    setLaunch(true);
    setActiveView('playing');
  }, [classeNom, xpProfil, eleveBDD.id]);

  const handleShowCorrection = (e) => {
    setQuestionsQuiz(getFilteredQuestions(e.questions));
    setEvalBilan(e);
    setLaunchCorrection(true);
    setActiveView('correction');
  };

  const handleBack = () => {
    setLaunch(false);
    setLaunchCorrection(false);
    setActiveView('dashboard');
  };

  // ─── Activités PLEIN ÉCRAN (sans tabbar) — inchangées ───
  if (activeView === 'playing' && launch) {
    return (
      <Quiz
        setLaunch={(val) => {
          setLaunch(val);
          // Fin d'un DÉFI → retour sur l'ÎLE (qui rouvre le cratère via le
          // flag sessionStorage posé au lancement) ; sinon accueil.
          if (!val) setActiveView(evalBilan?.source === 'defi' ? 'quizz' : 'dashboard');
        }}
        questions={questionsQuiz}
        evalBilan={evalBilan}
        eleveId={eleveBDD.id}
        classe_id={eleveBDD.classe}
      />
    );
  }

  if (activeView === 'correction' && launchCorrection) {
    return (
      <div className="eleve-gamified min-h-screen bg-slate-950 p-4">
        <button
          onClick={handleBack}
          className="mb-4 px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-bold flex items-center gap-2 hover:scale-105 transition-transform shadow-lg shadow-orange-500/30"
        >
          <ArrowLeft className="w-5 h-5" />
          Retour
        </button>
        <CorrectionEval evalBilan={evalBilan} eleveId={eleveBDD.id} />
      </div>
    );
  }

  // ─── Vues « maison » (avec tabbar persistante) ───
  const onChangeTab = (tab) => {
    if (tab === 'jouer') setActiveView('dashboard');
    else if (tab === 'ile') setActiveView('quizz');
    else setActiveView(tab);
  };

  let vue;
  if (activeView === 'quizz') {
    vue = (
      <IleEleve
        allQuizz={allQuizz}
        verifierSiEleveARepondu={verifierSiEleveARepondu}
        onBack={() => setActiveView('dashboard')}
        onStartQuiz={handleStartQuiz}
        onStartSession={handleStartSession}
        onShowCorrection={handleShowCorrection}
        onStartDefi={handleStartDefi}
        peutRejouer={peutRejouerQuiz}
        onStartRejeu={handleStartRejeu}
      />
    );
  } else if (activeView === 'resultats') {
    vue = (
      <ResultatViewEleve
        listEvalBilan={listEvalBilan}
        stats={stats}
        verifierSiEleveARepondu={verifierSiEleveARepondu}
        onBack={() => setActiveView('dashboard')}
        onShowCorrection={handleShowCorrection}
      />
    );
  } else if (activeView === 'blasons') {
    vue = <BlasonsEleve stats={stats} />;
  } else if (activeView === 'moi') {
    vue = <MoiEleve prenom={displayPrenom} />;
  } else {
    vue = (
      <HomeEleve
        prenom={displayPrenom}
        counts={counts}
        nextQuizTitre={nextQuiz?.titre || ''}
        onCommencer={() => setActiveView('quizz')}
        onOpenIle={() => setActiveView('quizz')}
        onResultats={() => setActiveView('resultats')}
      />
    );
  }

  return (
    <div
      className="eleve-gamified min-h-screen flex flex-col"
      style={{ background: 'linear-gradient(180deg,#0A1F3D,#122E54 62%,#0A1F3D)' }}
    >
      <HeaderEleve
        prenom={displayPrenom}
        classe={classeNom}
        niveau={xpProfil?.niveau}
        xp={xpProfil?.xp_total}
        progress={xpProfil ? Math.round(progressionRatio(xpProfil) * 100) : undefined}
        serie={xpProfil?.flamme_jours}
        etoiles={xpProfil?.etoiles_total}
        plume={xpProfil?.skin_plumage}
        acc={xpProfil?.skin_accessoire}
        aura={xpProfil?.skin_aura}
      />
      <main className="flex-1 flex flex-col pb-28">{vue}</main>
      <XpRecapOverlay open={!!recap} {...(recap || {})} onClose={clearRecap} />
      {/* Dock bas FIXE : footer légal (toujours visible) + tabbar */}
      <div className="fixed bottom-0 inset-x-0 z-40">
        <FooterEleve isGarEleve={isGarEleve} onLogout={handleLogout} />
        <TabBarEleve active={TAB_DE_VUE[activeView] || null} onChange={onChangeTab} />
      </div>
    </div>
  );
};

export default EspaceEleve;
